import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  ListGroup,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { FiList, FiPlus, FiTrash2, FiInfo, FiSliders, FiSave, FiCheck } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const ADMIN_CLIENT_ID = "68fe273606016adb4b0c5904";

const WhitelistModal = ({ show, onHide, monitor, clients }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("global");
  const [availableRegions, setAvailableRegions] = useState([]);
  const [hasRegions, setHasRegions] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [newPid, setNewPid] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkAdding, setBulkAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [togglingPreset, setTogglingPreset] = useState(false);
  const [includePreset, setIncludePreset] = useState(false);
  const [message, setMessage] = useState("");
  const [expandedPid, setExpandedPid] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ price_min: "", price_max: "", single_restock: true });
  const [savingFilters, setSavingFilters] = useState(false);
  const [helpDraft, setHelpDraft] = useState("");
  const [helpText, setHelpText] = useState("");
  const [editingHelp, setEditingHelp] = useState(false);
  const [savingHelp, setSavingHelp] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [applyFamily, setApplyFamily] = useState(false);
  const [selectedSiblingIds, setSelectedSiblingIds] = useState([]);

  const isTicket = monitor?.category === "Ticket";
  const isAdmin = (clients || []).some((c) => c._id === ADMIN_CLIENT_ID);
  const pidHelp = helpText.trim();

  const fetchAvailableRegions = useCallback(async () => {
    try {
      const res = await fetch(
        `${getHost()}/api/products/regions?monitor_id=${monitor._id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = await res.json();
      const regions = data.regions || [];
      setAvailableRegions(regions);
      setHasRegions(data.hasRegions || false);

      // If has regions, auto-select first one
      if (regions.length > 0) {
        setSelectedRegion(regions[0]);
      }
    } catch (error) {
      console.error("Failed to load regions");
    }
  }, [monitor]);

  // Regional siblings (same family + same category) for the "apply to whole
  // family" fan-out. Empty when the monitor has no family set.
  const fetchSiblings = useCallback(async () => {
    try {
      const res = await fetch(
        `${getHost()}/api/monitors/${monitor._id}/siblings`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data = await res.json();
      const sibs = data.siblings || [];
      setSiblings(sibs);
      setSelectedSiblingIds(sibs.map((s) => s.monitor_id));
    } catch (error) {
      setSiblings([]);
    }
  }, [monitor]);

  const fetchWhitelist = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(
        `${getHost()}/api/whitelist?monitor_id=${monitor._id}&client_id=${selectedClient}&region=${selectedRegion}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = await res.json();
      const items = data.items || (data.whitelist || []).map((pid) => ({ pid }));
      setWhitelist(items);
      setIncludePreset(data.include_preset ?? false);
    } catch (error) {
      setMessage("Failed to load whitelist");
    } finally {
      setLoading(false);
    }
  }, [monitor, selectedClient, selectedRegion]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedClient(defaultClientId(clients));
      setSelectedRegion("global");
      setAvailableRegions([]);
      setHasRegions(false);
      setWhitelist([]);
      setIncludePreset(false);
      setMessage("");
      setExpandedPid(null);
      const help = (monitor?.whitelist_pid_help || "").trim();
      setHelpText(help);
      setHelpDraft(help);
      setEditingHelp(false);
      setApplyFamily(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Fetch available regions and whitelist when client is selected
  useEffect(() => {
    if (!monitor || !selectedClient) return;

    fetchAvailableRegions();
  }, [monitor, selectedClient, fetchAvailableRegions]);

  // Fetch regional siblings when the monitor changes
  useEffect(() => {
    if (!monitor) return;
    fetchSiblings();
  }, [monitor, fetchSiblings]);

  // Fetch whitelist when region changes
  useEffect(() => {
    if (!monitor || !selectedClient) return;

    fetchWhitelist();
  }, [
    monitor,
    selectedClient,
    selectedRegion,
    fetchWhitelist,
  ]);

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!newPid.trim()) return;

    setAdding(true);
    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/whitelist/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pid: newPid.trim(),
          region: selectedRegion,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      // Optionally fan the same PID out to the selected regional siblings,
      // each tagged with its own region (handled server-side).
      let familyMsg = "";
      if (applyFamily && selectedSiblingIds.length > 0) {
        const mres = await fetch(`${getHost()}/api/whitelist/add-multi`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: selectedClient,
            pid: newPid.trim(),
            monitor_ids: selectedSiblingIds,
          }),
        });
        const mdata = await mres.json();
        if (mres.ok) {
          const results = mdata.results || [];
          const ok = results.filter((r) => r.ok).length;
          const invalid = results.filter((r) => !r.ok && r.error).map((r) => r.error);
          familyMsg = ` (+${ok} régions)${invalid.length ? ` ⚠️ ${invalid.join(", ")}` : ""}`;
        } else {
          familyMsg = " (⚠️ familles: échec)";
        }
      }

      await fetchWhitelist();
      setNewPid("");
      setMessage(`✅ PID added successfully!${familyMsg}`);

      if (!familyMsg.includes("⚠️")) setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to add PID"}`);
    } finally {
      setAdding(false);
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();

    const pids = bulkText
      .split(/[\s,]+/)
      .map((p) => p.trim())
      .filter((p) => p);
    if (pids.length === 0) return;

    setBulkAdding(true);
    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/whitelist/bulk-add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pids,
          region: selectedRegion,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      // Optionally fan the same PIDs out to the selected regional siblings,
      // each tagged with its own region.
      let familyMsg = "";
      if (applyFamily && selectedSiblingIds.length > 0) {
        const sibs = siblings.filter((s) =>
          selectedSiblingIds.includes(s.monitor_id),
        );
        const results = await Promise.all(
          sibs.map((s) =>
            fetch(`${getHost()}/api/whitelist/bulk-add`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${getToken()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                monitor_id: s.monitor_id,
                client_id: selectedClient,
                pids,
                region: s.region || "global",
              }),
            })
              .then((r) => r.ok)
              .catch(() => false),
          ),
        );
        const ok = results.filter(Boolean).length;
        familyMsg = ` (+${ok} régions)`;
      }

      await fetchWhitelist();
      setBulkText("");
      setBulkMode(false);
      const invalid = data.invalid_pids || [];
      setMessage(
        `✅ Added ${data.added} PID(s), skipped ${data.skipped}${familyMsg}` +
          (invalid.length ? ` ⚠️ Not valid on ${monitor.name}: ${invalid.join(", ")}` : ""),
      );

      if (!invalid.length) setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to bulk add PIDs"}`);
    } finally {
      setBulkAdding(false);
    }
  };

  const handleRemove = async (pid) => {
    if (!window.confirm(`Remove ${pid} from whitelist?`)) return;

    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/whitelist/remove`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pid,
          region: selectedRegion,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      if (expandedPid === pid) setExpandedPid(null);
      await fetchWhitelist();
      setMessage("✅ PID removed successfully!");

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to remove PID"}`);
    }
  };

  const handleTogglePreset = async (checked) => {
    setTogglingPreset(true);
    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/whitelist/toggle-preset`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          include_preset: checked,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      setIncludePreset(data.include_preset);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to toggle preset"}`);
    } finally {
      setTogglingPreset(false);
    }
  };

  const openFilterEditor = (item) => {
    if (expandedPid === item.pid) {
      setExpandedPid(null);
      return;
    }
    setExpandedPid(item.pid);
    setFilterDraft({
      price_min: item.price_min ?? "",
      price_max: item.price_max ?? "",
      single_restock: item.single_restock !== false,
    });
  };

  const handleSaveFilters = async (pid) => {
    setSavingFilters(true);
    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/whitelist/filters`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pid,
          region: selectedRegion,
          price_min: filterDraft.price_min === "" ? null : filterDraft.price_min,
          price_max: filterDraft.price_max === "" ? null : filterDraft.price_max,
          single_restock: filterDraft.single_restock,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      setExpandedPid(null);
      await fetchWhitelist();
      setMessage("✅ Filters saved!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to save filters"}`);
    } finally {
      setSavingFilters(false);
    }
  };

  const handleSaveHelp = async () => {
    setSavingHelp(true);
    setMessage("");

    try {
      const res = await fetch(
        `${getHost()}/api/monitors/${monitor._id}/whitelist-help`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ whitelist_pid_help: helpDraft }),
        },
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      const saved = (data.whitelist_pid_help || "").trim();
      if (monitor) monitor.whitelist_pid_help = saved;
      setHelpText(saved);
      setEditingHelp(false);
      setMessage("✅ Help saved!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to save example"}`);
    } finally {
      setSavingHelp(false);
    }
  };

  if (!monitor) return null;

  const modeBtnStyle = (active) => ({
    backgroundColor: active ? "#00E98E" : "transparent",
    border: `1px solid ${active ? "#00E98E" : "rgba(255,255,255,0.25)"}`,
    color: active ? "#0b1f17" : "rgba(255,255,255,0.55)",
    fontWeight: 600,
    fontSize: "12px",
  });

  const messageAlert = message && (
    <Alert
      variant={message.includes("✅") ? "success" : "danger"}
      dismissible
      onClose={() => setMessage("")}
      className="mb-3"
    >
      {message}
    </Alert>
  );

  const modeToggle = (
    <div className="btn-group btn-group-sm" role="group" aria-label="Add mode">
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center gap-1"
        style={modeBtnStyle(!bulkMode)}
        onClick={() => {
          setBulkMode(false);
          setBulkText("");
        }}
      >
        <FiPlus /> Single
      </button>
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center gap-1"
        style={modeBtnStyle(bulkMode)}
        onClick={() => {
          setBulkMode(true);
          setNewPid("");
        }}
      >
        <FiList /> Bulk
      </button>
    </div>
  );

  // Shared "apply to all available regions" fan-out block. Rendered in both
  // the single and bulk add forms. Empty when the monitor has no siblings.
  const familyBlock = siblings.length > 0 && (
    <div className="mt-2">
      <Form.Check
        type="checkbox"
        id="apply-family"
        label={`Apply to all available regions (${siblings.length})`}
        checked={applyFamily}
        onChange={(e) => setApplyFamily(e.target.checked)}
        className="text-main"
      />
      {applyFamily && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-2 ps-3">
            <span className="text-white-45 fs-12">
              {selectedSiblingIds.length}/{siblings.length} selected
            </span>
            <Button
              variant="link"
              size="sm"
              className="text-main p-0 fs-12 text-decoration-none"
              onClick={() =>
                setSelectedSiblingIds(
                  selectedSiblingIds.length === siblings.length
                    ? []
                    : siblings.map((s) => s.monitor_id),
                )
              }
            >
              {selectedSiblingIds.length === siblings.length
                ? "Deselect all"
                : "Select all"}
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-1 ps-3">
            {siblings.map((s) => {
              const sel = selectedSiblingIds.includes(s.monitor_id);
              return (
                <button
                  type="button"
                  key={s.monitor_id}
                  onClick={() =>
                    setSelectedSiblingIds((prev) =>
                      sel
                        ? prev.filter((x) => x !== s.monitor_id)
                        : [...prev, s.monitor_id],
                    )
                  }
                  className="d-flex align-items-center gap-1"
                  style={{
                    backgroundColor: sel ? "#00E98E" : "transparent",
                    border: `1px solid ${sel ? "#00E98E" : "rgba(255,255,255,0.25)"}`,
                    color: sel ? "#0b1f17" : "rgba(255,255,255,0.55)",
                    borderRadius: "999px",
                    padding: "3px 10px",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: 1.4,
                  }}
                >
                  {sel ? <FiCheck /> : <FiPlus />}
                  {s.name}
                  {s.region ? ` (${s.region})` : ""}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-secondary">
        <Modal.Title className="text-main d-flex align-items-center gap-2">
          <FiList className="fs-22" />
          <div>
            <div className="fs-18 fw-600">PID Whitelist</div>
            <div className="fs-14 text-white-45 mt-1">
              {monitor.name}
              {hasRegions && (
                <Badge bg="info" className="ms-2">
                  Multi-region
                </Badge>
              )}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-white modal-white-bg">
        {!selectedClient && messageAlert}

        {/* Client Selector */}
        <Form.Group className="mb-3">
          <Form.Label className="text-main">Select Group/Client</Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="form-input-field rounded-3 text-main custom-select-white"
          >
            <option value="">Select a group...</option>
            {clients.map((cl) => (
              <option key={cl._id} value={cl._id}>
                {cl.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Region Selector - ONLY show if monitor has regions */}
        {selectedClient && hasRegions && availableRegions.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label className="text-main">Select Region</Form.Label>
            <Form.Select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="form-input-field rounded-3 text-main custom-select-white"
            >
              {availableRegions.map((region) => (
                <option key={region} value={region}>
                  {region.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Per-site PID help — all sites. Clients see the green block; admins edit it behind a button. */}
        {(pidHelp || isAdmin) && (
          <div className="mb-3">
            {pidHelp && (
              <Alert
                className="mb-2"
                style={{
                  backgroundColor: "rgba(0, 233, 142, 0.1)",
                  border: "1px solid rgba(0, 233, 142, 0.3)",
                  color: "#ffffff",
                }}
              >
                <div className="d-flex align-items-start gap-2">
                  <FiInfo className="mt-1 flex-shrink-0" style={{ color: "#00E98E" }} />
                  <div className="w-100" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {pidHelp}
                  </div>
                </div>
              </Alert>
            )}

            {isAdmin && !editingHelp && (
              <Button
                variant="link"
                size="sm"
                className="text-white-45 p-0 fs-12 text-decoration-none"
                onClick={() => {
                  setHelpDraft(helpText);
                  setEditingHelp(true);
                }}
              >
                {pidHelp ? "✏️ Edit help" : "➕ Add help"}
              </Button>
            )}

            {isAdmin && editingHelp && (
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={helpDraft}
                  onChange={(e) => setHelpDraft(e.target.value)}
                  placeholder="PID help for this site…"
                  className="form-input-field"
                  disabled={savingHelp}
                />
                <div className="d-flex justify-content-end gap-2 mt-2">
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => {
                      setHelpDraft(helpText);
                      setEditingHelp(false);
                    }}
                    disabled={savingHelp}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveHelp}
                    disabled={savingHelp}
                    className="d-flex align-items-center gap-2"
                    style={{ backgroundColor: "#4A4A4A", borderColor: "#4A4A4A" }}
                  >
                    {savingHelp ? <Spinner size="sm" /> : <><FiSave /> Save</>}
                  </Button>
                </div>
              </Form.Group>
            )}
          </div>
        )}

        {selectedClient && (
          <>
            {/* Include Preset Toggle */}
            <Form.Group className="mb-3">
              <div
                className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <div>
                  <div className="text-main fs-14 fw-600">
                    Include preset pings
                  </div>
                  <div className="text-white-45 fs-12">
                    Receive all default pings in addition to whitelisted PIDs
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  checked={includePreset}
                  onChange={(e) => handleTogglePreset(e.target.checked)}
                  disabled={togglingPreset}
                />
              </div>
            </Form.Group>

            {/* Add PID Form */}
            {bulkMode ? (
              <Form onSubmit={handleBulkAdd} className="mb-4">
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="text-main mb-0">
                      Bulk Add PIDs
                    </Form.Label>
                    {modeToggle}
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="Paste PIDs — one per line or comma-separated"
                    className="form-input-field font-monospace"
                    disabled={bulkAdding}
                  />
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={bulkAdding || !bulkText.trim()}
                      className="d-flex align-items-center gap-2"
                      style={{
                        backgroundColor: "#4A4A4A",
                        borderColor: "#4A4A4A",
                        minWidth: "120px",
                      }}
                    >
                      {bulkAdding ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <FiPlus /> Add All
                        </>
                      )}
                    </Button>
                  </div>
                </Form.Group>
                {familyBlock}
              </Form>
            ) : (
              <Form onSubmit={handleAdd} className="mb-4">
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="text-main mb-0">
                      Add PID to Whitelist
                    </Form.Label>
                    {modeToggle}
                  </div>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={newPid}
                      onChange={(e) => setNewPid(e.target.value)}
                      placeholder="Enter PID (e.g., DV3854-200)"
                      className="form-input-field"
                      disabled={adding}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={adding || !newPid.trim()}
                      className="d-flex align-items-center gap-2"
                      style={{
                        backgroundColor: "#4A4A4A",
                        borderColor: "#4A4A4A",
                        minWidth: "80px",
                      }}
                    >
                      {adding ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <FiPlus /> Add
                        </>
                      )}
                    </Button>
                  </div>
                </Form.Group>

                {familyBlock}
              </Form>
            )}

            {messageAlert}

            {/* Whitelist Display */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="text-main mb-0">
                  Whitelisted PIDs ({whitelist.length})
                </Form.Label>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="light" size="sm" />
                </div>
              ) : whitelist.length === 0 ? (
                <div className="text-center py-4 text-white-65">
                  No PIDs whitelisted yet
                </div>
              ) : (
                <div className="whitelist-pid-list">
                  <ListGroup variant="flush">
                    {whitelist.map((item, index) => {
                      const hasFilters =
                        item.price_min != null ||
                        item.price_max != null ||
                        item.single_restock === false;
                      return (
                        <ListGroup.Item
                          key={index}
                          className="bg-transparent border-secondary text-white px-0 py-2"
                        >
                          <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1 min-w-0">
                              <code className="text-main text-break">{item.pid}</code>
                              {item.price_min != null && (
                                <Badge bg="secondary">≥ {item.price_min}</Badge>
                              )}
                              {item.price_max != null && (
                                <Badge bg="secondary">≤ {item.price_max}</Badge>
                              )}
                              {item.single_restock === false && (
                                <Badge bg="secondary">restock &gt; 1</Badge>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                              {isTicket && (
                                <Button
                                  variant={
                                    expandedPid === item.pid
                                      ? "primary"
                                      : hasFilters
                                        ? "outline-primary"
                                        : "outline-light"
                                  }
                                  size="sm"
                                  title="Filters"
                                  onClick={() => openFilterEditor(item)}
                                >
                                  <FiSliders />
                                </Button>
                              )}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemove(item.pid)}
                              >
                                <FiTrash2 />
                              </Button>
                            </div>
                          </div>

                          {isTicket && expandedPid === item.pid && (
                            <div
                              className="mt-2 p-3 rounded-3"
                              style={{
                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              <div className="text-white-45 fs-12 mb-2">
                                Filters only decide whether a ping fires — not who
                                receives it.
                              </div>
                              <div className="d-flex gap-2 mb-2">
                                <Form.Group className="w-100">
                                  <Form.Label className="text-main fs-12 mb-1">
                                    Min price
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    value={filterDraft.price_min}
                                    onChange={(e) =>
                                      setFilterDraft((d) => ({
                                        ...d,
                                        price_min: e.target.value,
                                      }))
                                    }
                                    placeholder="—"
                                    className="form-input-field"
                                  />
                                </Form.Group>
                                <Form.Group className="w-100">
                                  <Form.Label className="text-main fs-12 mb-1">
                                    Max price
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    value={filterDraft.price_max}
                                    onChange={(e) =>
                                      setFilterDraft((d) => ({
                                        ...d,
                                        price_max: e.target.value,
                                      }))
                                    }
                                    placeholder="—"
                                    className="form-input-field"
                                  />
                                </Form.Group>
                              </div>
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="text-main fs-13">
                                  Only ping when more than 1 seat is available
                                </div>
                                <Form.Check
                                  type="switch"
                                  checked={filterDraft.single_restock === false}
                                  onChange={(e) =>
                                    setFilterDraft((d) => ({
                                      ...d,
                                      single_restock: !e.target.checked,
                                    }))
                                  }
                                />
                              </div>
                              <div className="d-flex justify-content-end">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={savingFilters}
                                  onClick={() => handleSaveFilters(item.pid)}
                                  className="d-flex align-items-center gap-2"
                                >
                                  {savingFilters ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <FiSave /> Save filters
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button
          variant="secondary"
          onClick={onHide}
          className="rounded-3 py-2 px-3 fw-600 fs-12"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WhitelistModal;
